import Grid from '@mui/material/Grid';
import { type InventoryAndContentState, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent';
import { Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { useTranslation } from 'react-i18next';
import type { AnyAction } from 'redux';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine';
import { FiltersTitle } from '../../shared/FiltersTitle/FiltersTitle';
import { INVENTORY_IDS_DISPLAY_LIMIT, useInventoryAndContentLabels } from '../../shared/InventoryAndContent/constants';
import {
  resetFilter,
  setComparisonOpValue,
  setContentCategoryOp,
} from '../../shared/InventoryAndContent/inventoryHelpers';
import { SelectedItems } from '../../shared/SelectedItems/SelectedItems';
import { Switch } from '../../shared/Switch/Switch';
import type { TargetingItemsProps } from '../../types';

export const KeywordItems = ({
  dispatchInventory = noop,
  readonly = true,
  targetingParams,
  isNotLastItem,
  switchState: chipLineSwitchState,
  showDimensionChipLine = true,
  disabledReasonMessage,
}: TargetingItemsProps<InventoryAndContentState>) => {
  const { t } = useTranslation();

  const optionPayload = targetingParams[InventoryContentOption.KEYWORDS];
  const { op: dimension, val } = optionPayload;

  const labels = useInventoryAndContentLabels();
  const label = labels[InventoryContentOption.KEYWORDS].label;

  const switchState = dimension === Intersect.NOT_INTERSECTS;
  const filtersTitle = dimension === Intersect.NOT_INTERSECTS ? t('Excluded') : t('Included');
  const itemsCount = val.length;

  return (
    <Grid data-test="sub-section">
      <FiltersTitle
        onClear={() =>
          dispatchInventory({
            payload: {
              contentOption: InventoryContentOption.KEYWORDS,
            },
            type: resetFilter,
          } as unknown as AnyAction)
        }
        title={`${label} [ ${itemsCount} ]`}
        readonly={readonly}
        disabledReasonMessage={disabledReasonMessage}
      />
      {!readonly && (
        <Switch
          switchState={switchState}
          onSwitchChange={() =>
            dispatchInventory({
              payload: switchState ? Intersect.INTERSECTS : Intersect.NOT_INTERSECTS,
              type: setContentCategoryOp[InventoryContentOption.KEYWORDS],
            } as unknown as AnyAction)
          }
          switchOffLabel={t('include')}
          switchOnLabel={t('exclude')}
        />
      )}

      <Grid data-test="section-items">
        <SelectedItems
          filtersTitle={filtersTitle}
          isAddIcon={dimension === Intersect.INTERSECTS}
          selectedItems={val}
          readonly={readonly}
          handleChange={values => {
            dispatchInventory({
              payload: values,
              type: setComparisonOpValue[InventoryContentOption.KEYWORDS],
            } as unknown as AnyAction);
          }}
          isRemovingAll={true}
          customDisplayLimit={INVENTORY_IDS_DISPLAY_LIMIT}
          dataTest="keywords"
        />
      </Grid>
      {isNotLastItem && showDimensionChipLine && <DimensionChipLine switchState={chipLineSwitchState} />}
    </Grid>
  );
};
